// Job metrics and the activity/stats page: live state of the GPU servers, summaries per model and person,
// recent jobs (other people's redacted).
import { z } from "zod";
import { JobStatus, ModelKind, RedactedJob } from "./core";
import { GpuSample, RunnerInfo } from "./fleet";

export const StatsRange = z.enum(["24h", "7d"]);
export type StatsRange = z.infer<typeof StatsRange>;

/**
 * How speed is measured per kind:
 * - image: seconds of generation per picture (lower is faster)
 * - video: seconds of generation per second of output video (lower is faster), per resolution
 * - music: realtime factor = seconds of music per second of generation (higher is faster)
 * - speech: realtime factor = seconds of speech per second of generation (higher is faster)
 * - enhance: seconds of generation per megapixel written, counted over every frame of a video (lower is faster)
 * - chat: output tokens per second after the first token (higher is faster)
 */
export const SpeedUnit = z.enum([
  "sec_per_image",
  "sec_per_output_sec",
  "realtime_factor",
  "tokens_per_sec",
  "sec_per_megapixel",
]);
export type SpeedUnit = z.infer<typeof SpeedUnit>;

export const SPEED_UNIT_FOR: Record<ModelKind, SpeedUnit> = {
  image: "sec_per_image",
  video: "sec_per_output_sec",
  music: "realtime_factor",
  speech: "realtime_factor",
  enhance: "sec_per_megapixel",
  chat: "tokens_per_sec",
};

export const Distribution = z.object({ avg: z.number().nullable(), median: z.number().nullable() });
export type Distribution = z.infer<typeof Distribution>;

export const VariantStats = z.object({
  /** Video: resolution ("720p", "720p fast" when the step cache ran); other kinds do not have variants. */
  key: z.string(),
  count: z.number().int(),
  completed: z.number().int(),
  generateSec: Distribution,
  speed: Distribution,
  avgOutputSec: z.number().nullable(),
  avgTokens: z.number().nullable(),
});
export type VariantStats = z.infer<typeof VariantStats>;

export const ModelStats = z.object({
  model: z.string(),
  kind: ModelKind,
  count: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
  cancelled: z.number().int(),
  /** Queue wait until a GPU server took it. */
  waitSec: Distribution,
  /** Model loads (only jobs that needed one). */
  loadSec: Distribution,
  loads: z.number().int(),
  generateSec: Distribution,
  /** Queued → done. */
  totalSec: Distribution,
  speedUnit: SpeedUnit,
  speed: Distribution,
  /** Chat: time to first token. */
  ttftSec: Distribution.nullable(),
  variants: z.array(VariantStats),
});
export type ModelStats = z.infer<typeof ModelStats>;

export const UserStats = z.object({
  userId: z.string(),
  displayName: z.string(),
  jobs: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
  chats: z.number().int(),
  /** Seconds the GPU servers spent on this person's work (load + generation). */
  gpuSec: z.number(),
  byKind: z.record(z.string(), z.number().int()),
});
export type UserStats = z.infer<typeof UserStats>;

export const RunnerStats = z.object({
  runnerId: z.string(),
  runnerName: z.string(),
  jobs: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
  gpuSec: z.number(),
});
export type RunnerStats = z.infer<typeof RunnerStats>;

/** GET /api/stats/summary?range=24h|7d */
export const StatsSummary = z.object({
  range: StatsRange,
  from: z.string(),
  to: z.string(),
  totals: z.object({
    jobs: z.number().int(),
    completed: z.number().int(),
    failed: z.number().int(),
    cancelled: z.number().int(),
    chats: z.number().int(),
    gpuSec: z.number(),
    avgWaitSec: z.number().nullable(),
    activeUsers: z.number().int(),
  }),
  models: z.array(ModelStats),
  runners: z.array(RunnerStats),
  /** Admins only (null for everyone else). */
  users: z.array(UserStats).nullable(),
  /** The viewer's own totals. */
  mine: z.object({
    jobs: z.number().int(),
    completed: z.number().int(),
    failed: z.number().int(),
    chats: z.number().int(),
    gpuSec: z.number(),
  }),
});
export type StatsSummary = z.infer<typeof StatsSummary>;

/** GET /api/stats/jobs: one finished (or failed/cancelled) job or chat answer. */
export const JobStat = z.object({
  ref: z.string(),
  mine: z.boolean(),
  /** Only for the viewer's own jobs (null for chats and other people's jobs). */
  jobId: z.string().nullable(),
  kind: ModelKind,
  model: z.string(),
  status: JobStatus,
  ownerName: z.string(),
  runnerName: z.string().nullable(),
  slotId: z.string().nullable(),
  /** Video resolution. */
  variant: z.string().nullable(),
  queuedAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  waitSec: z.number().nullable(),
  loadSec: z.number().nullable(),
  generateSec: z.number().nullable(),
  totalSec: z.number().nullable(),
  /** Seconds of video or music produced. */
  outputSec: z.number().nullable(),
  /** Video: H3 latent tokens. */
  tokens: z.number().nullable(),
  images: z.number().nullable(),
  speed: z.number().nullable(),
  speedUnit: SpeedUnit,
  promptTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
  ttftSec: z.number().nullable(),
  /** Only for the viewer's own jobs. */
  error: z.string().nullable(),
});
export type JobStat = z.infer<typeof JobStat>;

/** GET /api/stats/live */
export const StatsLive = z.object({
  at: z.string(),
  runners: z.array(RunnerInfo),
  /** Every unfinished job of everyone, in queue order; own jobs carry `jobId`. */
  queue: z.array(RedactedJob),
  gpus: z.array(GpuSample),
  chat: z.object({
    /** slot: answered by a runner's chat model; external: CHAT_BASE_URL; none: no chat model anywhere. */
    mode: z.enum(["slot", "external", "none"]),
    streaming: z.number().int(),
    waiting: z.number().int(),
  }),
});
export type StatsLive = z.infer<typeof StatsLive>;
